import calendarItem from 'owa-service/lib/factory/calendarItem';
import DeleteItemField from 'owa-service/lib/contract/DeleteItemField';
import deleteItemField from 'owa-service/lib/factory/deleteItemField';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import SetItemField from 'owa-service/lib/contract/SetItemField';
import setItemField from 'owa-service/lib/factory/setItemField';
import { CalendarEvent } from 'mini-calendar-event-types';
import { getEwsRequestString, OwaDate } from 'owa-datetime';

/**
 * TODO: Update code according to new logic in client-web
 */

/**
 * The interface used in mapping CalendarEvent properties to SetItemField or DeleteItemField
 */
interface PropertyDefinition {
    /**
     * Generally the server expects same property names as in CalendarEvent, but in case if a property name is different,
     * we need to use fieldUri to send the exact property name the server is using
     */
    fieldUri?: string;

    /**
     * The function if provided can be used to convert a given value to value understandable to server
     */
    getEwsValue?: (this: void, value: any) => any;

    /**
     * Whether we can use deleteItemField to reset this property. Example: CharmId
     */
    useDeleteItemField?: boolean;
}

function getEwsDate(date: OwaDate) {
    return date ? getEwsRequestString(date) : null;
}

/**
 * Map CalendarEvent properties to a PropertyDefinition.
 * Properties not in this map are considered not supported.
 * NOTE: Tried to keep this map consistent with big OWA
 */
const propertyMap: Partial<Record<keyof CalendarEvent, PropertyDefinition>> = {
    Start: { getEwsValue: getEwsDate },
    End: { getEwsValue: getEwsDate },
    Subject: {},
    Body: {},
    IsAllDayEvent: {},
    IsDraft: {},
    Locations: {},
    Recurrence: { useDeleteItemField: true },
    FreeBusyType: { fieldUri: 'LegacyFreeBusyStatus' },
    ReminderMinutesBeforeStart: {},
    ReminderIsSet: {},
    CharmId: { fieldUri: 'Charm', useDeleteItemField: true },
    Sensitivity: {},
    Categories: { fieldUri: 'Categories' },
    IsResponseRequested: { fieldUri: 'CalendarIsResponseRequested' },
    DoNotForwardMeeting: {},
    RequiredAttendees: {},
    IsOnlineMeeting: {},
    OnlineMeetingProvider: {},
    OptionalAttendees: {},
    InboxReminders: { getEwsValue: nullifyIfEmpty, useDeleteItemField: true },
    Resources: {},
    StartTimeZoneId: {},
    EndTimeZoneId: {},
};

/**
 * Takes the key/value of the property to be updated and returns a SetItemField.
 * TODO: At present this handles updates for a few properties, need to implement the updates as we need them in the UI
 * @param propertyName name of the property to be updated in the calendar item object
 * @param value Value of the property to be updated
 */
export function getUpdateForProperty(
    propertyName: keyof CalendarEvent,
    value: any
): SetItemField | DeleteItemField | null {
    const mapping = propertyMap[propertyName];

    if (mapping) {
        const { fieldUri = propertyName as string, getEwsValue, useDeleteItemField } = mapping;

        const path = propertyUri({ FieldURI: fieldUri });
        const ewsValue = getEwsValue ? getEwsValue(value) : value;

        if (!useDeleteItemField || ewsValue) {
            return setItemField({
                Path: path,
                Item: calendarItem({
                    [propertyName]: ewsValue,
                }),
            });
        } else {
            return deleteItemField({
                Path: path,
            });
        }
    }

    return null;
}

function nullifyIfEmpty(arr: any[]) {
    return !arr || arr.length === 0 ? null : arr;
}
